// frontend/src/screens/admin/Dashboard/SchedulingDashboard.jsx

import React, { useState, useMemo } from 'react';
import {
  Box, Grid, Paper, Typography, FormControl, InputLabel, Select,
  MenuItem, TextField, Chip, Divider, Tooltip, CircularProgress,
  Alert, Button, IconButton,
} from '@mui/material';
import { useSelector } from 'react-redux';
import RefreshIcon from '@mui/icons-material/Refresh';
import PrintIcon from '@mui/icons-material/Print';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import GroupIcon from '@mui/icons-material/Group';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import BusinessIcon from '@mui/icons-material/Business';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import AssessmentIcon from '@mui/icons-material/Assessment';

import {
  useGetWeeklyShiftsByStaffQuery,
  useGetAvailableShiftsQuery,
} from '../../../slices/shiftApiSlice';
import {
  useGetWeeklyAvailabilitiesByStaffQuery,
} from '../../../slices/availabilityApiSlice';
import { useGetHospitalLocationsQuery } from '../../../slices/hospitalLocationApiSlice';
import { useGetUsersQuery } from '../../../slices/usersApiSlice';

// ─── Palette ────────────────────────────────────────────────────────────────
const NAVY   = '#0d1b2a';
const TEAL   = '#0a8577';
const TEAL_L = '#0cb89e';
const AMBER  = '#e8a020';
const RED    = '#c0392b';
const GREEN  = '#27ae60';
const SLATE  = '#1e2d3d';
const MUTED  = '#8fa3b1';
const WHITE  = '#f0f4f8';

// ─── Helpers ────────────────────────────────────────────────────────────────
const pct = (n, d) => (d === 0 ? 0 : Math.round((n / d) * 100));
const fmt  = (mins) => {
  if (!mins) return '0h';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
};
const shiftMins = (shift) => {
  if (!shift.startDateTime || !shift.endDateTime) return 0;
  return Math.round((new Date(shift.endDateTime) - new Date(shift.startDateTime)) / 60000);
};

// ─── Sub-components ──────────────────────────────────────────────────────────

const SectionTitle = ({ icon: Icon, label }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5, mt: 1 }}>
    <Box sx={{ width: 4, height: 28, bgcolor: TEAL, borderRadius: 1 }} />
    <Icon sx={{ color: TEAL, fontSize: 22 }} />
    <Typography sx={{ fontFamily: '"DM Mono", monospace', fontWeight: 700, fontSize: '0.85rem', letterSpacing: 2, textTransform: 'uppercase', color: WHITE }}>
      {label}
    </Typography>
  </Box>
);

const StatCard = ({ label, value, sub, color = TEAL, icon: Icon, tooltip }) => (
  <Paper elevation={0} sx={{
    bgcolor: SLATE, border: `1px solid ${color}22`, borderRadius: 2,
    p: 2.5, position: 'relative', overflow: 'hidden', height: '100%',
    '&::before': { content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: 3, bgcolor: color },
    transition: 'transform 0.2s, box-shadow 0.2s',
    '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 8px 24px ${color}22` },
  }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
      <Box>
        <Typography sx={{ fontSize: '0.7rem', fontFamily: '"DM Mono", monospace', letterSpacing: 1.5, textTransform: 'uppercase', color: MUTED, mb: 1 }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: '2rem', fontFamily: '"DM Mono", monospace', fontWeight: 700, color: WHITE, lineHeight: 1 }}>
          {value}
        </Typography>
        {sub && (
          <Typography sx={{ fontSize: '0.72rem', color: MUTED, mt: 0.75, fontFamily: '"DM Mono", monospace' }}>
            {sub}
          </Typography>
        )}
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
        {Icon && <Icon sx={{ color: `${color}99`, fontSize: 28 }} />}
        {tooltip && (
          <Tooltip title={tooltip} arrow>
            <InfoOutlinedIcon sx={{ color: MUTED, fontSize: 14, cursor: 'help' }} />
          </Tooltip>
        )}
      </Box>
    </Box>
  </Paper>
);

const BarRow = ({ label, value, total, color, showPct = true }) => {
  const p = pct(value, total);
  return (
    <Box sx={{ mb: 1.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography sx={{ fontSize: '0.75rem', color: WHITE, fontFamily: '"DM Mono", monospace' }}>{label}</Typography>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: WHITE, fontFamily: '"DM Mono", monospace' }}>{value}</Typography>
          {showPct && <Typography sx={{ fontSize: '0.7rem', color: MUTED, fontFamily: '"DM Mono", monospace', minWidth: 36, textAlign: 'right' }}>{p}%</Typography>}
        </Box>
      </Box>
      <Box sx={{ height: 6, bgcolor: `${color}22`, borderRadius: 1, overflow: 'hidden' }}>
        <Box sx={{ height: '100%', width: `${p}%`, bgcolor: color, borderRadius: 1, transition: 'width 0.6s ease' }} />
      </Box>
    </Box>
  );
};

const GaugeRing = ({ value, label, color = TEAL, size = 100 }) => {
  const r = 40;
  const circ = 2 * Math.PI * r;
  const dash = (value / 100) * circ;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
      <svg width={size} height={size} viewBox="0 0 100 100">
        <circle cx="50" cy="50" r={r} fill="none" stroke={`${color}22`} strokeWidth="10" />
        <circle cx="50" cy="50" r={r} fill="none" stroke={color} strokeWidth="10"
          strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
          transform="rotate(-90 50 50)" style={{ transition: 'stroke-dasharray 0.8s ease' }} />
        <text x="50" y="54" textAnchor="middle" fill={WHITE}
          style={{ fontSize: 18, fontFamily: '"DM Mono", monospace', fontWeight: 700 }}>
          {value}%
        </text>
      </svg>
      <Typography sx={{ fontSize: '0.68rem', color: MUTED, fontFamily: '"DM Mono", monospace', textAlign: 'center', letterSpacing: 1, textTransform: 'uppercase' }}>
        {label}
      </Typography>
    </Box>
  );
};

const FilterSelect = ({ label, value, onChange, options, disabled }) => (
  <FormControl size="small" sx={{ minWidth: 160,
    '& .MuiOutlinedInput-root': { bgcolor: SLATE, color: WHITE, borderRadius: 1.5,
      '& fieldset': { borderColor: `${TEAL}44` },
      '&:hover fieldset': { borderColor: TEAL },
      '&.Mui-focused fieldset': { borderColor: TEAL },
    },
    '& .MuiInputLabel-root': { color: MUTED },
    '& .MuiSvgIcon-root': { color: MUTED },
  }}>
    <InputLabel sx={{ color: MUTED, fontSize: '0.8rem' }}>{label}</InputLabel>
    <Select value={value} label={label} onChange={e => onChange(e.target.value)} disabled={disabled}
      sx={{ fontSize: '0.8rem', fontFamily: '"DM Mono", monospace' }}
      MenuProps={{
        PaperProps: {
          sx: {
            bgcolor: SLATE,
            border: `1px solid ${TEAL}44`,
            '& .MuiMenuItem-root': {
              color: WHITE,
              fontFamily: '"DM Mono", monospace',
              fontSize: '0.8rem',
              '&:hover': {
                bgcolor: `${TEAL}44`,
                color: WHITE,
              },
              '&.Mui-selected': {
                bgcolor: `${TEAL}33`,
                color: TEAL_L,
                '&:hover': {
                  bgcolor: `${TEAL}55`,
                },
              },
            },
          },
        },
      }}
    >
      <MenuItem value="">
        <em style={{ color: MUTED }}>All</em>
      </MenuItem>
      {options.map(o => (
        <MenuItem key={o} value={o}>{o}</MenuItem>
      ))}
    </Select>
  </FormControl>
);



// ─── Main Component ──────────────────────────────────────────────────────────
const SchedulingDashboard = () => {
  const { userInfo } = useSelector(s => s.auth);

  // Default date range: current month
  const now = new Date();
  const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
  const lastOfMonth  = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];

  const [dateFrom, setDateFrom] = useState(firstOfMonth);
  const [dateTo,   setDateTo]   = useState(lastOfMonth);
  const [fDirectorate,   setFDirectorate]   = useState('');
  const [fWard,          setFWard]          = useState('');
  const [fStaffCategory, setFStaffCategory] = useState('');

  // Build ISO range for queries
  const startDate    = dateFrom || firstOfMonth;
  const endDateISO   = dateTo   ? new Date(dateTo + 'T23:59:59').toISOString() : new Date(lastOfMonth + 'T23:59:59').toISOString();

  const { data: hospitalLocations = [] } = useGetHospitalLocationsQuery();
  const { data: usersData = [] }         = useGetUsersQuery();

  const {
    data: staffShiftsRaw = [],
    isLoading: loadingStaffShifts,
    refetch: refetchStaffShifts,
  } = useGetWeeklyShiftsByStaffQuery({ startDate, endDate: endDateISO });

  const {
    data: availableShiftsRaw = [],
    isLoading: loadingAvailShifts,
    refetch: refetchAvailShifts,
  } = useGetAvailableShiftsQuery({ startDate, endDate: endDateISO });

  const {
    data: availabilitiesRaw = [],
    isLoading: loadingAvail,
    refetch: refetchAvail,
  } = useGetWeeklyAvailabilitiesByStaffQuery({ startDate, endDate: endDateISO });

  const isLoading = loadingStaffShifts || loadingAvailShifts || loadingAvail;

  const refetchAll = () => { refetchStaffShifts(); refetchAvailShifts(); refetchAvail(); };

  // ── Print handler ──────────────────────────────────────────────────────────
  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) { return; }

    const filterSummary = [
      dateFrom && dateTo ? `${dateFrom} to ${dateTo}` : '',
      fDirectorate ? `Directorate: ${fDirectorate}` : '',
      fWard        ? `Ward: ${fWard}` : '',
      fStaffCategory ? `Category: ${fStaffCategory}` : '',
    ].filter(Boolean).join(' · ') || 'No filters applied';

    // Helper to build a horizontal bar as inline HTML
    const bar = (value, total, color) => {
      const p = total === 0 ? 0 : Math.round((value / total) * 100);
      return `<div style="height:7px;background:#e8ecf0;border-radius:4px;overflow:hidden;margin-top:3px;">
        <div style="height:100%;width:${p}%;background:${color};border-radius:4px;"></div>
      </div>`;
    };

    const statBox = (label, value, sub, color) => `
      <div style="background:#f2f2f2;border:1px solid #d0d0d0;border-top:3px solid ${color};border-radius:8px;padding:14px 16px;min-width:120px;flex:1;">
        <div style="font-size:9px;letter-spacing:1.5px;text-transform:uppercase;color:#555;margin-bottom:6px;font-family:'Courier New',monospace;">${label}</div>
        <div style="font-size:24px;font-weight:700;color:#111;line-height:1;font-family:'Courier New',monospace;">${value}</div>
        ${sub ? `<div style="font-size:9px;color:#555;margin-top:4px;font-family:'Courier New',monospace;">${sub}</div>` : ''}
      </div>`;

    const kpiBox = (label, value, unit, note, color) => `
      <div style="background:#fff;border:1px solid #e0e7ef;border-radius:8px;padding:14px 16px;flex:1;min-width:140px;">
        <div style="font-size:9px;letter-spacing:1.5px;text-transform:uppercase;color:#8fa3b1;margin-bottom:6px;font-family:'Courier New',monospace;">${label}</div>
        <div style="font-size:28px;font-weight:700;color:${color};line-height:1;font-family:'Courier New',monospace;">${value}${unit || ''}</div>
        <div style="font-size:9px;color:#8fa3b1;margin-top:4px;font-family:'Courier New',monospace;">${note}</div>
      </div>`;

    const sectionTitle = (label) => `
      <div style="display:flex;align-items:center;gap:10px;margin:28px 0 14px;">
        <div style="width:4px;height:20px;background:#0a8577;border-radius:2px;flex-shrink:0;"></div>
        <div style="font-size:9px;font-weight:700;letter-spacing:2.5px;text-transform:uppercase;color:#0d1b2a;font-family:'Courier New',monospace;">${label}</div>
        <div style="flex:1;height:1px;background:#e0e7ef;"></div>
      </div>`;

    const barRow = (label, value, total, color) => {
      const p = total === 0 ? 0 : Math.round((value / total) * 100);
      return `<div style="margin-bottom:10px;">
        <div style="display:flex;justify-content:space-between;margin-bottom:3px;">
          <span style="font-size:10px;color:#0d1b2a;font-family:'Courier New',monospace;">${label}</span>
          <span style="font-size:10px;font-weight:700;color:#0d1b2a;font-family:'Courier New',monospace;">${value} <span style="color:#8fa3b1;font-weight:400;">(${p}%)</span></span>
        </div>
        ${bar(value, total, color)}
      </div>`;
    };

    // ── RAG label helper ────────────────────────────────────────────────────
    const ragLabel = (val, goodThresh, warnThresh, higherIsBetter, goodMsg, warnMsg, badMsg) => {
      const isGood = higherIsBetter ? val >= goodThresh : val <= goodThresh;
      const isWarn = higherIsBetter ? val >= warnThresh : val <= warnThresh;
      const color  = isGood ? '#27ae60' : isWarn ? '#e8a020' : '#c0392b';
      const msg    = isGood ? goodMsg   : isWarn ? warnMsg   : badMsg;
      return `<div style="margin-top:8px;padding:5px 8px;background:${color}18;border-radius:4px;font-size:9px;color:${color};font-family:'Courier New',monospace;">${msg}</div>`;
    };

    const html = `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8">
    <title>Scheduling Analytics Report — ${filterSummary}</title>
    <style>
      @page { size: A4 portrait; margin: 15mm 12mm; }
      * { box-sizing: border-box; margin: 0; padding: 0; }
      body { font-family: 'Courier New', monospace; background: #fff; color: #0d1b2a; font-size: 11px; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .page { padding: 0; }
      .flex-row { display: flex; gap: 10px; flex-wrap: wrap; margin-bottom: 10px; }
      .card { background: #f2f2f2; border: 1px solid #d0d0d0; border-radius: 8px; padding: 14px; }
      .label { font-size: 9px; letter-spacing: 1.5px; text-transform: uppercase; color: #555; }
      table { width: 100%; border-collapse: collapse; font-size: 10px; }
      th { background: #f0f4f8; padding: 7px 10px; text-align: left; font-weight: 700; letter-spacing: 0.5px; border-bottom: 2px solid #e0e7ef; font-size: 9px; text-transform: uppercase; }
      td { padding: 6px 10px; border-bottom: 1px solid #f0f4f8; }
      tr:last-child td { border-bottom: none; }
      .badge { display: inline-block; padding: 2px 7px; border-radius: 10px; font-size: 9px; font-weight: 700; }
      @media print { body { padding: 0; } }
    </style></head>
    <body><div class="page">

      <!-- REPORT HEADER -->
      <div style="background:#0d1b2a;padding:20px 24px;border-radius:10px;margin-bottom:20px;">
        <div style="display:flex;justify-content:space-between;align-items:flex-start;">
          <div>
            <div style="font-size:18px;font-weight:700;color:#f0f4f8;letter-spacing:1px;">Scheduling Analytics Report</div>
            <div style="font-size:10px;color:#0cb89e;margin-top:4px;letter-spacing:2px;">BEAUMONT HOSPITAL · STAFF SCHEDULING SYSTEM</div>
          </div>
          <div style="text-align:right;">
            <div style="font-size:9px;color:#8fa3b1;">Generated</div>
            <div style="font-size:11px;color:#f0f4f8;font-weight:700;">${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}</div>
            <div style="font-size:9px;color:#8fa3b1;margin-top:4px;">By: ${userInfo?.name || 'System'}</div>
          </div>
        </div>
        <div style="margin-top:14px;padding:10px 14px;background:rgba(255,255,255,0.07);border-radius:6px;border-left:3px solid #0a8577;">
          <div style="font-size:9px;color:#8fa3b1;letter-spacing:1px;text-transform:uppercase;margin-bottom:3px;">Filters Applied</div>
          <div style="font-size:10px;color:#f0f4f8;">${filterSummary}</div>
        </div>
        <div style="display:flex;gap:16px;margin-top:12px;">
          <div style="font-size:10px;color:#0cb89e;font-weight:700;">${filteredShifts.length} shifts</div>
          <div style="font-size:10px;color:#e8a020;font-weight:700;">${filteredAvail.length} availability records</div>
          <div style="font-size:10px;color:#8fa3b1;">Period: ${dateFrom || 'All'} → ${dateTo || 'All'}</div>
        </div>
      </div>

      <!-- SECTION 1: SHIFT OVERVIEW -->
      ${sectionTitle('1 · Shift Overview')}
      <div class="flex-row">
        ${statBox('Total Shifts',       shiftStats.total,     `${fmt(shiftStats.totalMins)} requested`,                           '#0a8577')}
        ${statBox('Filled — Staff',     shiftStats.filled,    `${shiftStats.pctFilled}% · ${fmt(shiftStats.filledMins)}`,          '#27ae60')}
        ${statBox('Filled — Agency',    shiftStats.agency,    `${shiftStats.pctAgency}% · ${fmt(shiftStats.agencyMins)}`,          '#e8a020')}
        ${statBox('Open / Unfilled',    shiftStats.open,      `${shiftStats.pctOpen}% · ${fmt(shiftStats.openMins)}`,              '#c0392b')}
        ${statBox('Cancelled',          shiftStats.cancelled, `${shiftStats.pctCancelled}%`,                                       '#95a5a6')}
        ${statBox('Staff Deployed',     shiftStats.staffCount,'Unique individuals',                                                '#0cb89e')}
      </div>

      <!-- Shift status bars -->
      <div style="display:flex;gap:10px;margin-bottom:10px;">
        <div class="card" style="flex:1.4;">
          <div class="label" style="margin-bottom:10px;">Shift Status Breakdown</div>
          ${barRow('Filled — Staff',  shiftStats.filled,    shiftStats.total, '#27ae60')}
          ${barRow('Filled — Agency', shiftStats.agency,    shiftStats.total, '#e8a020')}
          ${barRow('Open / Unfilled', shiftStats.open,      shiftStats.total, '#c0392b')}
          ${barRow('Cancelled',       shiftStats.cancelled, shiftStats.total, '#95a5a6')}
          <div style="height:1px;background:#e0e7ef;margin:10px 0;"></div>
          ${barRow('Weekday Shifts',  shiftStats.weekday,   shiftStats.total, '#0a8577')}
          ${barRow('Weekend Shifts',  shiftStats.weekend,   shiftStats.total, '#e8a020')}
        </div>

        <div class="card" style="flex:1;">
          <div class="label" style="margin-bottom:10px;">Shifts by Ward (Top 6)</div>
          ${shiftStats.byWard.length === 0
            ? '<div style="color:#8fa3b1;font-size:10px;">No data</div>'
            : shiftStats.byWard.map(([ward, d]) => `
              <div style="margin-bottom:9px;">
                <div style="display:flex;justify-content:space-between;margin-bottom:2px;">
                  <span style="font-size:9px;color:#0d1b2a;max-width:110px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${ward}</span>
                  <span style="font-size:9px;color:#8fa3b1;">${d.filled}s / ${d.agency}a / ${d.total}t</span>
                </div>
                <div style="height:6px;background:#e8ecf0;border-radius:3px;overflow:hidden;display:flex;">
                  <div style="width:${pct(d.filled,d.total)}%;background:#27ae60;"></div>
                  <div style="width:${pct(d.agency,d.total)}%;background:#e8a020;"></div>
                  <div style="width:${pct(Math.max(0,d.total-d.filled-d.agency),d.total)}%;background:#c0392b;"></div>
                </div>
              </div>`).join('')}
          <div style="display:flex;gap:10px;margin-top:6px;">
            ${[['Staff','#27ae60'],['Agency','#e8a020'],['Open','#c0392b']].map(([l,c]) =>
              `<span style="font-size:8px;color:${c};">■ ${l}</span>`).join('')}
          </div>
        </div>
      </div>

      <!-- SECTION 2: HOURS SUMMARY -->
      ${sectionTitle('2 · Hours Summary')}
      <div class="flex-row" style="margin-bottom:20px;">
        ${statBox('Hours Requested',  fmt(shiftStats.totalMins),  'All shifts combined',                                          '#0a8577')}
        ${statBox('Hours — Staff',    fmt(shiftStats.filledMins),  `${pct(shiftStats.filledMins, shiftStats.totalMins)}% of requested`, '#27ae60')}
        ${statBox('Hours — Agency',   fmt(shiftStats.agencyMins),  `${pct(shiftStats.agencyMins, shiftStats.totalMins)}% of requested`, '#e8a020')}
        ${statBox('Hours — Unfilled', fmt(shiftStats.openMins),    `${pct(shiftStats.openMins, shiftStats.totalMins)}% of requested`,   '#c0392b')}
      </div>

      <!-- SECTION 3: AVAILABILITY OVERVIEW -->
      ${sectionTitle('3 · Availability Overview')}
      <div class="flex-row">
        ${statBox('Avail. Records',  availStats.available,  `${fmt(availStats.totalMins)} offered`,                             '#0a8577')}
        ${statBox('Approved',        availStats.approved,   `${availStats.pctApproved}% · ${fmt(availStats.approvedMins)}`,     '#27ae60')}
        ${statBox('Pending',         availStats.pending,    `${availStats.pctPending}% of submitted`,                           '#e8a020')}
        ${statBox('Rejected',        availStats.rejected,   `${availStats.pctRejected}% of submitted`,                          '#c0392b')}
        ${statBox('Unavailability',  availStats.unavail,    `${fmt(availStats.unavailMins)} blocked`,                            '#7f8c8d')}
        ${statBox('Sick Leave',      availStats.sick,       `${fmt(availStats.sickMins)} absent`,                                '#c0392b')}
      </div>

      <div style="display:flex;gap:10px;margin-bottom:10px;">
        <div class="card" style="flex:1;">
          <div class="label" style="margin-bottom:10px;">Availability Status</div>
          ${barRow('Approved',       availStats.approved, availStats.available || 1, '#27ae60')}
          ${barRow('Pending',        availStats.pending,  availStats.available || 1, '#e8a020')}
          ${barRow('Rejected',       availStats.rejected, availStats.available || 1, '#c0392b')}
          <div style="height:1px;background:#e0e7ef;margin:10px 0;"></div>
          ${barRow('Unavailability', availStats.unavail,  (availStats.available + availStats.unavail + availStats.sick) || 1, '#7f8c8d')}
          ${barRow('Sick Leave',     availStats.sick,     (availStats.available + availStats.unavail + availStats.sick) || 1, '#c0392b')}
          <div style="margin-top:10px;font-size:9px;color:#555;">Staff with availability: <strong style="color:#111;">${availStats.staffWithAvailCount}</strong></div>
        </div>

        <div class="card" style="flex:1;">
          <div class="label" style="margin-bottom:10px;">Utilisation</div>
          <div style="display:flex;align-items:center;gap:14px;margin-bottom:12px;">
            <div>
              <div style="font-size:36px;font-weight:700;color:${availStats.utilisationPct >= 80 ? '#27ae60' : availStats.utilisationPct >= 50 ? '#e8a020' : '#c0392b'};line-height:1;">${availStats.utilisationPct}%</div>
              <div style="font-size:9px;color:#8fa3b1;margin-top:3px;">Availability Utilisation</div>
            </div>
            <div style="flex:1;">
              <div style="height:10px;background:#e8ecf0;border-radius:5px;overflow:hidden;">
                <div style="height:100%;width:${availStats.utilisationPct}%;background:${availStats.utilisationPct >= 80 ? '#27ae60' : availStats.utilisationPct >= 50 ? '#e8a020' : '#c0392b'};border-radius:5px;"></div>
              </div>
            </div>
          </div>
          <div style="display:flex;justify-content:space-between;margin-bottom:5px;">
            <span style="font-size:9px;color:#555;">Approved availability</span>
            <span style="font-size:9px;font-weight:700;color:#111;">${fmt(availStats.approvedMins)}</span>
          </div>
          <div style="display:flex;justify-content:space-between;margin-bottom:10px;">
            <span style="font-size:9px;color:#555;">Staff hours filled</span>
            <span style="font-size:9px;font-weight:700;color:#27ae60;">${fmt(shiftStats.filledMins)}</span>
          </div>
          ${coverageGap.gapMins > 0
            ? `<div style="padding:6px 8px;background:#c0392b18;border-radius:4px;font-size:9px;color:#c0392b;">⚠ ${fmt(coverageGap.gapMins)} demand exceeds approved availability</div>`
            : `<div style="padding:6px 8px;background:#27ae6018;border-radius:4px;font-size:9px;color:#27ae60;">✓ ${fmt(coverageGap.surplusMins)} surplus availability over demand</div>`}
          <div style="margin-top:8px;font-size:8px;color:#555;">Staff-filled hours ÷ approved availability hours × 100</div>
        </div>

        <div class="card" style="flex:1;">
          <div class="label" style="margin-bottom:10px;">Top Unavailability Reasons</div>
          ${availStats.unavailReasons.length === 0
            ? '<div style="font-size:10px;color:#8fa3b1;">None recorded</div>'
            : availStats.unavailReasons.map(([reason, count]) =>
                barRow(reason, count, availStats.unavail + availStats.sick || 1, '#c0392b')
              ).join('')}
          <div style="height:1px;background:#d0d0d0;margin:10px 0;"></div>
          <div style="display:flex;justify-content:space-between;">
            <span style="font-size:9px;color:#555;">Total unavailability hours</span>
            <span style="font-size:9px;font-weight:700;color:#c0392b;">${fmt(availStats.unavailMins + availStats.sickMins)}</span>
          </div>
        </div>
      </div>

      <!-- SECTION 4: KPIs -->
      ${sectionTitle('4 · Key Performance Indicators')}
      <div class="flex-row" style="margin-bottom:20px;">
        <div class="card" style="flex:1;min-width:140px;">
          <div class="label">Agency Dependency</div>
          <div style="font-size:30px;font-weight:700;color:${shiftStats.pctAgency > 30 ? '#c0392b' : shiftStats.pctAgency > 15 ? '#e8a020' : '#27ae60'};margin:6px 0 2px;">${shiftStats.pctAgency}%</div>
          <div style="font-size:9px;color:#555;">${shiftStats.agency} of ${shiftStats.total} shifts via agency</div>
          ${ragLabel(shiftStats.pctAgency, 15, 30, false, '✓ WITHIN TARGET', '△ MODERATE — monitor closely', '⚠ HIGH — review staffing levels')}
        </div>
        <div class="card" style="flex:1;min-width:140px;">
          <div class="label">Unfilled Shift Rate</div>
          <div style="font-size:30px;font-weight:700;color:${shiftStats.pctOpen > 20 ? '#c0392b' : shiftStats.pctOpen > 10 ? '#e8a020' : '#27ae60'};margin:6px 0 2px;">${shiftStats.pctOpen}%</div>
          <div style="font-size:9px;color:#555;">${shiftStats.open} open · ${fmt(shiftStats.openMins)} uncovered</div>
          ${ragLabel(shiftStats.pctOpen, 10, 20, false, '✓ WITHIN ACCEPTABLE RANGE', '△ MODERATE — action needed', '⚠ HIGH — escalate to management')}
        </div>
        <div class="card" style="flex:1;min-width:140px;">
          <div class="label">Internal Coverage</div>
          <div style="font-size:30px;font-weight:700;color:${shiftStats.pctFilled >= 70 ? '#27ae60' : shiftStats.pctFilled >= 50 ? '#e8a020' : '#c0392b'};margin:6px 0 2px;">${shiftStats.pctFilled}%</div>
          <div style="font-size:9px;color:#555;">${shiftStats.filled} shifts · ${shiftStats.staffCount} staff deployed</div>
          ${ragLabel(shiftStats.pctFilled, 70, 50, true, '✓ STRONG INTERNAL COVERAGE', '△ MODERATE — review allocation', '⚠ LOW — review rostering')}
        </div>
        <div class="card" style="flex:1;min-width:140px;">
          <div class="label">Avail. Utilisation</div>
          <div style="font-size:30px;font-weight:700;color:${availStats.utilisationPct >= 70 ? '#27ae60' : availStats.utilisationPct >= 40 ? '#e8a020' : '#c0392b'};margin:6px 0 2px;">${availStats.utilisationPct}%</div>
          <div style="font-size:9px;color:#555;">${fmt(shiftStats.filledMins)} used of ${fmt(availStats.approvedMins)}</div>
          ${ragLabel(availStats.utilisationPct, 70, 40, true, '✓ EFFICIENT USE OF AVAILABILITY', '△ MODERATE UTILISATION', '⚠ LOW — availability not converting to shifts')}
        </div>
      </div>

      <!-- SECTION 5: BY STAFF CATEGORY -->
      ${shiftStats.byCategory.length > 0 ? `
        ${sectionTitle('5 · By Staff Category')}
        <div style="display:flex;gap:10px;margin-bottom:20px;">
          <div class="card" style="flex:1.5;">
            <div class="label" style="margin-bottom:10px;">Shifts by Category</div>
            ${shiftStats.byCategory.map(([cat, d]) => barRow(cat, d.filled, d.total, '#27ae60')).join('')}
          </div>
          <div class="card" style="flex:1;">
            <table>
              <thead><tr><th>Category</th><th>Total</th><th>Filled</th><th>Fill %</th><th>Hours</th></tr></thead>
              <tbody>
                ${shiftStats.byCategory.map(([cat, d]) => {
                  const hrs = fmt(filteredShifts.filter(s => s.categoryOfStaff === cat).reduce((a, s) => a + shiftMins(s), 0));
                  return `<tr>
                    <td>${cat}</td>
                    <td>${d.total}</td>
                    <td>${d.filled}</td>
                    <td><span class="badge" style="background:${pct(d.filled,d.total)>=70?'#27ae6022':'#e8a02022'};color:${pct(d.filled,d.total)>=70?'#27ae60':'#e8a020'};">${pct(d.filled,d.total)}%</span></td>
                    <td>${hrs}</td>
                  </tr>`;
                }).join('')}
              </tbody>
            </table>
          </div>
        </div>` : ''}

      <!-- SUMMARY TABLE -->
      ${sectionTitle('Summary')}
      <table style="margin-bottom:24px;">
        <thead><tr><th>Metric</th><th>Value</th><th>Note</th></tr></thead>
        <tbody>
          <tr><td>Total shifts in period</td><td>${shiftStats.total}</td><td>${fmt(shiftStats.totalMins)} total hours requested</td></tr>
          <tr><td>Shifts filled by staff</td><td>${shiftStats.filled} (${shiftStats.pctFilled}%)</td><td>${fmt(shiftStats.filledMins)} hours</td></tr>
          <tr><td>Shifts filled by agency</td><td>${shiftStats.agency} (${shiftStats.pctAgency}%)</td><td>${fmt(shiftStats.agencyMins)} hours</td></tr>
          <tr><td>Open / unfilled shifts</td><td>${shiftStats.open} (${shiftStats.pctOpen}%)</td><td>${fmt(shiftStats.openMins)} hours uncovered</td></tr>
          <tr><td>Cancelled shifts</td><td>${shiftStats.cancelled} (${shiftStats.pctCancelled}%)</td><td>—</td></tr>
          <tr><td>Weekend shifts</td><td>${shiftStats.weekend} (${shiftStats.pctWeekend}%)</td><td>${shiftStats.weekday} weekday</td></tr>
          <tr><td>Unique staff deployed</td><td>${shiftStats.staffCount}</td><td>Internal staff only</td></tr>
          <tr><td>Availability records</td><td>${availStats.available}</td><td>${fmt(availStats.totalMins)} offered</td></tr>
          <tr><td>Approved availability</td><td>${availStats.approved} (${availStats.pctApproved}%)</td><td>${fmt(availStats.approvedMins)}</td></tr>
          <tr><td>Pending availability</td><td>${availStats.pending} (${availStats.pctPending}%)</td><td>Awaiting review</td></tr>
          <tr><td>Unavailability records</td><td>${availStats.unavail}</td><td>${fmt(availStats.unavailMins)} blocked</td></tr>
          <tr><td>Sick leave records</td><td>${availStats.sick}</td><td>${fmt(availStats.sickMins)} absent</td></tr>
          <tr><td>Availability utilisation</td><td>${availStats.utilisationPct}%</td><td>Staff-filled ÷ approved availability</td></tr>
          ${coverageGap.gapMins > 0
            ? `<tr><td>Coverage gap</td><td style="color:#c0392b;">${fmt(coverageGap.gapMins)}</td><td>Demand exceeds approved availability</td></tr>`
            : `<tr><td>Availability surplus</td><td style="color:#27ae60;">${fmt(coverageGap.surplusMins)}</td><td>Availability exceeds demand</td></tr>`}
        </tbody>
      </table>

      <!-- FOOTER -->
      <div style="border-top:2px solid #0d1b2a;padding-top:10px;display:flex;justify-content:space-between;align-items:center;">
        <div style="font-size:8px;color:#8fa3b1;letter-spacing:1px;">BEAUMONT HOSPITAL STAFF SCHEDULING SYSTEM</div>
        <div style="font-size:8px;color:#8fa3b1;">Generated ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()} by ${userInfo?.name || 'System'}</div>
        <div style="font-size:8px;color:#8fa3b1;">beaumontNursingBank@beaumont.ie</div>
      </div>

    </div>
    <script>window.onload=function(){setTimeout(()=>{window.print();setTimeout(()=>window.close(),200);},600);};</script>
    </body></html>`;

    printWindow.document.write(html);
    printWindow.document.close();
  };

  // ── Filter option lists ────────────────────────────────────────────────────
  const directorates = useMemo(() => [...new Set(hospitalLocations.map(l => l.directorate).filter(Boolean))].sort(), [hospitalLocations]);
  const wards = useMemo(() => {
    const base = fDirectorate
      ? hospitalLocations.filter(l => l.directorate === fDirectorate)
      : hospitalLocations;
    return [...new Set(base.map(l => l.wardDepartmentArea).filter(Boolean))].sort();
  }, [hospitalLocations, fDirectorate]);
  const staffCategories = useMemo(() => [...new Set([...staffShiftsRaw, ...availableShiftsRaw].map(s => s.categoryOfStaff).filter(Boolean))].sort(), [staffShiftsRaw, availableShiftsRaw]);

  // ── Combine all shifts (assigned + available) ──────────────────────────────
  const allShifts = useMemo(() => {
    const assigned  = staffShiftsRaw || [];
    const available = availableShiftsRaw || [];
    // Deduplicate by _id
    const map = new Map();
    [...assigned, ...available].forEach(s => { if (s._id) map.set(s._id, s); });
    return [...map.values()];
  }, [staffShiftsRaw, availableShiftsRaw]);

  // ── Apply filters to shifts ────────────────────────────────────────────────
  const filteredShifts = useMemo(() => allShifts.filter(s => {
    if (fDirectorate   && s.directorate       !== fDirectorate)   return false;
    if (fWard          && s.wardDepartmentArea !== fWard)          return false;
    if (fStaffCategory && s.categoryOfStaff   !== fStaffCategory) return false;
    return true;
  }), [allShifts, fDirectorate, fWard, fStaffCategory]);

  // ── Apply filters to availabilities ───────────────────────────────────────
  const filteredAvail = useMemo(() => (availabilitiesRaw || []).filter(a => {
    if (fWard          && a.wardDepartmentArea !== fWard)          return false;
    if (fStaffCategory && a.categoryOfStaff   !== fStaffCategory) return false;
    return true;
  }), [availabilitiesRaw, fWard, fStaffCategory]);

  // ── Shift stats ────────────────────────────────────────────────────────────
  const shiftStats = useMemo(() => {
    const total    = filteredShifts.length;
    const filled   = filteredShifts.filter(s => s.filledBy && !s.filledByAgency && s.lineStatus !== 'Cancelled');
    const agency   = filteredShifts.filter(s => s.filledByAgency);
    const cancelled= filteredShifts.filter(s => s.lineStatus === 'Cancelled');
    const open     = filteredShifts.filter(s => !s.filledBy && !s.filledByAgency && s.lineStatus !== 'Cancelled');
    const totalMins= filteredShifts.reduce((acc, s) => acc + shiftMins(s), 0);
    const filledMins  = filled.reduce((acc, s)  => acc + shiftMins(s), 0);
    const agencyMins  = agency.reduce((acc, s)  => acc + shiftMins(s), 0);
    const openMins    = open.reduce((acc, s)    => acc + shiftMins(s), 0);

    // Unique staff count
    const staffIds = new Set(filled.map(s => s.filledBy?._id || s.filledBy).filter(Boolean));

    // Weekend shifts
    const weekend = filteredShifts.filter(s => {
      const d = new Date(s.startDateTime).getDay();
      return d === 0 || d === 6;
    });

    // By ward
    const byWard = filteredShifts.reduce((acc, s) => {
      const k = s.wardDepartmentArea || 'Unknown';
      if (!acc[k]) acc[k] = { total: 0, filled: 0, agency: 0 };
      acc[k].total++;
      if (s.filledBy && !s.filledByAgency) acc[k].filled++;
      if (s.filledByAgency) acc[k].agency++;
      return acc;
    }, {});

    // By category
    const byCategory = filteredShifts.reduce((acc, s) => {
      const k = s.categoryOfStaff || 'Unknown';
      if (!acc[k]) acc[k] = { total: 0, filled: 0 };
      acc[k].total++;
      if (s.filledBy) acc[k].filled++;
      return acc;
    }, {});

    return {
      total, filled: filled.length, agency: agency.length,
      cancelled: cancelled.length, open: open.length,
      totalMins, filledMins, agencyMins, openMins,
      staffCount: staffIds.size,
      weekend: weekend.length, weekday: total - weekend.length,
      pctFilled:    pct(filled.length,    total),
      pctAgency:    pct(agency.length,    total),
      pctCancelled: pct(cancelled.length, total),
      pctOpen:      pct(open.length,      total),
      pctWeekend:   pct(weekend.length,   total),
      byWard: Object.entries(byWard).sort((a,b) => b[1].total - a[1].total).slice(0, 6),
      byCategory: Object.entries(byCategory).sort((a,b) => b[1].total - a[1].total),
    };
  }, [filteredShifts]);

  // ── Availability stats ─────────────────────────────────────────────────────
  const availStats = useMemo(() => {
    const total     = filteredAvail.length;
    const approved  = filteredAvail.filter(a => a.status === 'Approved' && !a.isUnavailable && !a.isSickLeave);
    const pending   = filteredAvail.filter(a => a.status === 'Pending'  && !a.isUnavailable && !a.isSickLeave);
    const rejected  = filteredAvail.filter(a => a.status === 'Rejected' && !a.isUnavailable && !a.isSickLeave);
    const unavail   = filteredAvail.filter(a => a.isUnavailable && !a.isSickLeave);
    const sick      = filteredAvail.filter(a => a.isSickLeave);
    const trueAvail = [...approved, ...pending, ...rejected];

    const totalMins    = trueAvail.reduce((acc, a)  => acc + (a.durationMinutes || 0), 0);
    const approvedMins = approved.reduce((acc, a)   => acc + (a.durationMinutes || 0), 0);
    const unavailMins  = unavail.reduce((acc, a)    => acc + (a.durationMinutes || 0), 0);
    const sickMins     = sick.reduce((acc, a)       => acc + (a.durationMinutes || 0), 0);

    // Utilisation = staff-filled shift hours / approved availability hours
    const utilisationPct = approvedMins > 0 ? Math.min(100, pct(shiftStats.filledMins, approvedMins)) : 0;

    // Top unavailability reasons
    const unavailReasons = unavail.concat(sick).reduce((acc, a) => {
      const k = a.unavailabilityReason || a.sicknessReason || (a.isSickLeave ? 'Sick Leave' : 'Other');
      acc[k] = (acc[k] || 0) + 1;
      return acc;
    }, {});

    // Unique staff with availability
    const staffWithAvail = new Set(
      trueAvail.map(a => (typeof a.requestedBy === 'object' ? a.requestedBy?._id : a.requestedBy)).filter(Boolean)
    );

    return {
      total,
      available: trueAvail.length,
      approved: approved.length, pending: pending.length, rejected: rejected.length,
      unavail: unavail.length, sick: sick.length,
      totalMins, approvedMins, unavailMins, sickMins,
      utilisationPct,
      staffWithAvailCount: staffWithAvail.size,
      pctApproved: pct(approved.length,  trueAvail.length),
      pctPending:  pct(pending.length,   trueAvail.length),
      pctRejected: pct(rejected.length,  trueAvail.length),
      pctUnavail:  pct(unavail.length + sick.length, total),
      unavailReasons: Object.entries(unavailReasons).sort((a,b) => b[1]-a[1]).slice(0,6),
    };
  }, [filteredAvail, shiftStats.filledMins]);

  // ── Coverage gap ───────────────────────────────────────────────────────────
  const coverageGap = useMemo(() => {
    // Hours of shifts needing cover vs hours of approved availability
    const gapMins = Math.max(0, shiftStats.totalMins - availStats.approvedMins);
    return { gapMins, surplusMins: Math.max(0, availStats.approvedMins - shiftStats.totalMins) };
  }, [shiftStats.totalMins, availStats.approvedMins]);

  const isAdmin   = userInfo?.isAdmin;
  const isManager = userInfo?.isManager;

  return (
    <Box sx={{
      minHeight: '100vh', bgcolor: NAVY, p: { xs: 2, md: 3 },
      fontFamily: '"DM Mono", "Courier New", monospace',
    }}>
      {/* Google Font import */}
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Mono:wght@400;500;700&display=swap');`}</style>

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
            <AssessmentIcon sx={{ color: TEAL, fontSize: 28 }} />
            <Typography sx={{ fontFamily: '"DM Mono", monospace', fontSize: { xs: '1.3rem', md: '1.7rem' }, fontWeight: 700, color: WHITE, letterSpacing: 1 }}>
              Scheduling Analytics
            </Typography>
          </Box>
          <Typography sx={{ color: MUTED, fontSize: '0.75rem', fontFamily: '"DM Mono", monospace', letterSpacing: 1.5 }}>
            BEAUMONT HOSPITAL · STAFF SCHEDULING SYSTEM
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Chip label={`${filteredShifts.length} shifts`} size="small" sx={{ bgcolor: `${TEAL}22`, color: TEAL_L, fontFamily: '"DM Mono", monospace', fontSize: '0.7rem', border: `1px solid ${TEAL}44` }} />
          <Chip label={`${filteredAvail.length} availability records`} size="small" sx={{ bgcolor: `${AMBER}22`, color: AMBER, fontFamily: '"DM Mono", monospace', fontSize: '0.7rem', border: `1px solid ${AMBER}44` }} />
          <Tooltip title="Print report">
            <Button
              onClick={handlePrint}
              disabled={isLoading}
              size="small"
              startIcon={<PrintIcon sx={{ fontSize: 16 }} />}
              sx={{
                bgcolor: `${TEAL}22`, color: TEAL_L,
                border: `1px solid ${TEAL}44`,
                fontFamily: '"DM Mono", monospace',
                fontSize: '0.72rem', letterSpacing: 1,
                textTransform: 'uppercase',
                px: 1.5, py: 0.75,
                '&:hover': { bgcolor: TEAL, color: WHITE },
              }}
            >
              Print Report
            </Button>
          </Tooltip>
          <Tooltip title="Refresh data">
            <IconButton onClick={refetchAll} size="small" sx={{ color: MUTED, '&:hover': { color: TEAL_L, bgcolor: `${TEAL}22` } }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* ── Filters ────────────────────────────────────────────────────────── */}
      <Paper elevation={0} sx={{ bgcolor: SLATE, border: `1px solid ${TEAL}22`, borderRadius: 2, p: 2, mb: 3 }}>
        <Typography sx={{ fontSize: '0.68rem', letterSpacing: 2, textTransform: 'uppercase', color: MUTED, fontFamily: '"DM Mono", monospace', mb: 1.5 }}>
          Filters
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <TextField size="small" label="From" type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
            InputLabelProps={{ shrink: true, style: { color: MUTED, fontSize: '0.8rem' } }}
            sx={{ '& .MuiOutlinedInput-root': { bgcolor: NAVY, color: WHITE, '& fieldset': { borderColor: `${TEAL}44` }, '&:hover fieldset': { borderColor: TEAL } }, '& input': { fontFamily: '"DM Mono", monospace', fontSize: '0.8rem' } }} />
          <TextField size="small" label="To" type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
            InputLabelProps={{ shrink: true, style: { color: MUTED, fontSize: '0.8rem' } }}
            sx={{ '& .MuiOutlinedInput-root': { bgcolor: NAVY, color: WHITE, '& fieldset': { borderColor: `${TEAL}44` }, '&:hover fieldset': { borderColor: TEAL } }, '& input': { fontFamily: '"DM Mono", monospace', fontSize: '0.8rem' } }} />
          {(isAdmin) && (
            <FilterSelect label="Directorate" value={fDirectorate} onChange={v => { setFDirectorate(v); setFWard(''); }} options={directorates} />
          )}
          <FilterSelect label="Ward / Dept" value={fWard} onChange={setFWard} options={wards} />
          <FilterSelect label="Staff Category" value={fStaffCategory} onChange={setFStaffCategory} options={staffCategories} />
          {(fDirectorate || fWard || fStaffCategory) && (
            <Button size="small" onClick={() => { setFDirectorate(''); setFWard(''); setFStaffCategory(''); }}
              sx={{ color: MUTED, fontFamily: '"DM Mono", monospace', fontSize: '0.7rem', '&:hover': { color: WHITE } }}>
              Clear filters
            </Button>
          )}
        </Box>
      </Paper>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
          <CircularProgress sx={{ color: TEAL }} />
        </Box>
      ) : (
        <Box>

          {/* ══ SECTION 1: Shift Overview ══════════════════════════════════ */}
          <SectionTitle icon={ScheduleIcon} label="Shift Overview" />
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={4} md={2}>
              <StatCard label="Total Shifts" value={shiftStats.total} icon={ScheduleIcon} color={TEAL} sub={`${fmt(shiftStats.totalMins)} requested`} tooltip="All shifts in the selected period and filters" />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <StatCard label="Filled by Staff" value={shiftStats.filled} icon={GroupIcon} color={GREEN} sub={`${shiftStats.pctFilled}% of total · ${fmt(shiftStats.filledMins)}`} tooltip="Shifts filled by internal staff" />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <StatCard label="Filled by Agency" value={shiftStats.agency} icon={BusinessIcon} color={AMBER} sub={`${shiftStats.pctAgency}% of total · ${fmt(shiftStats.agencyMins)}`} tooltip="Shifts filled by agency staff" />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <StatCard label="Open / Unfilled" value={shiftStats.open} icon={EventAvailableIcon} color={RED} sub={`${shiftStats.pctOpen}% of total · ${fmt(shiftStats.openMins)}`} tooltip="Shifts still requiring cover" />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <StatCard label="Cancelled" value={shiftStats.cancelled} icon={CancelOutlinedIcon} color={`${RED}99`} sub={`${shiftStats.pctCancelled}% of total`} tooltip="Shifts cancelled in this period" />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <StatCard label="Staff Deployed" value={shiftStats.staffCount} icon={GroupIcon} color={TEAL_L} sub="Unique individuals" tooltip="Count of unique staff members assigned at least one shift" />
            </Grid>
          </Grid>

          {/* Shift breakdown bars + gauges */}
          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid item xs={12} md={5}>
              <Paper elevation={0} sx={{ bgcolor: SLATE, border: `1px solid ${TEAL}22`, borderRadius: 2, p: 2.5, height: '100%' }}>
                <Typography sx={{ fontSize: '0.7rem', letterSpacing: 1.5, textTransform: 'uppercase', color: MUTED, fontFamily: '"DM Mono", monospace', mb: 2 }}>Shift Status Breakdown</Typography>
                <BarRow label="Filled — Staff"  value={shiftStats.filled}    total={shiftStats.total} color={GREEN} />
                <BarRow label="Filled — Agency" value={shiftStats.agency}    total={shiftStats.total} color={AMBER} />
                <BarRow label="Open / Unfilled" value={shiftStats.open}      total={shiftStats.total} color={RED} />
                <BarRow label="Cancelled"       value={shiftStats.cancelled} total={shiftStats.total} color={`${RED}88`} />
                <Divider sx={{ borderColor: `${TEAL}22`, my: 1.5 }} />
                <BarRow label="Weekday Shifts"  value={shiftStats.weekday}   total={shiftStats.total} color={TEAL} />
                <BarRow label="Weekend Shifts"  value={shiftStats.weekend}   total={shiftStats.total} color={AMBER} />
              </Paper>
            </Grid>

            <Grid item xs={12} md={3}>
              <Paper elevation={0} sx={{ bgcolor: SLATE, border: `1px solid ${TEAL}22`, borderRadius: 2, p: 2.5, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'space-around' }}>
                <Typography sx={{ fontSize: '0.7rem', letterSpacing: 1.5, textTransform: 'uppercase', color: MUTED, fontFamily: '"DM Mono", monospace', mb: 1, alignSelf: 'flex-start' }}>Fill Rate</Typography>
                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', justifyContent: 'center', pt: 1 }}>
                  <GaugeRing value={shiftStats.pctFilled}  label="Staff Fill"   color={GREEN} />
                  <GaugeRing value={shiftStats.pctAgency}  label="Agency Fill"  color={AMBER} />
                  <GaugeRing value={shiftStats.pctOpen}    label="Unfilled"     color={RED} />
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} md={4}>
              <Paper elevation={0} sx={{ bgcolor: SLATE, border: `1px solid ${TEAL}22`, borderRadius: 2, p: 2.5, height: '100%' }}>
                <Typography sx={{ fontSize: '0.7rem', letterSpacing: 1.5, textTransform: 'uppercase', color: MUTED, fontFamily: '"DM Mono", monospace', mb: 2 }}>Shifts by Ward (Top 6)</Typography>
                {shiftStats.byWard.length === 0
                  ? <Typography sx={{ color: MUTED, fontSize: '0.75rem', fontFamily: '"DM Mono", monospace' }}>No data</Typography>
                  : shiftStats.byWard.map(([ward, d]) => (
                    <Box key={ward} sx={{ mb: 1.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.4 }}>
                        <Typography sx={{ fontSize: '0.7rem', color: WHITE, fontFamily: '"DM Mono", monospace', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ward}</Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Typography sx={{ fontSize: '0.7rem', color: GREEN, fontFamily: '"DM Mono", monospace' }}>{d.filled}s</Typography>
                          <Typography sx={{ fontSize: '0.7rem', color: AMBER, fontFamily: '"DM Mono", monospace' }}>{d.agency}a</Typography>
                          <Typography sx={{ fontSize: '0.7rem', color: MUTED, fontFamily: '"DM Mono", monospace' }}>{d.total}t</Typography>
                        </Box>
                      </Box>
                      <Box sx={{ height: 5, bgcolor: `${TEAL}22`, borderRadius: 1, overflow: 'hidden', display: 'flex' }}>
                        <Box sx={{ width: `${pct(d.filled, d.total)}%`, bgcolor: GREEN }} />
                        <Box sx={{ width: `${pct(d.agency, d.total)}%`, bgcolor: AMBER }} />
                        <Box sx={{ width: `${pct(d.total - d.filled - d.agency, d.total)}%`, bgcolor: RED }} />
                      </Box>
                    </Box>
                  ))}
                <Box sx={{ display: 'flex', gap: 2, mt: 1.5 }}>
                  {[['Staff',GREEN],['Agency',AMBER],['Open',RED]].map(([l,c]) => (
                    <Box key={l} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 8, height: 8, bgcolor: c, borderRadius: '50%' }} />
                      <Typography sx={{ fontSize: '0.62rem', color: MUTED, fontFamily: '"DM Mono", monospace' }}>{l}</Typography>
                    </Box>
                  ))}
                </Box>
              </Paper>
            </Grid>
          </Grid>

          {/* ══ SECTION 2: Hours Summary ════════════════════════════════════ */}
          <SectionTitle icon={TrendingUpIcon} label="Hours Summary" />
          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid item xs={6} sm={3}>
              <StatCard label="Hours Requested" value={fmt(shiftStats.totalMins)} color={TEAL} sub="All shifts (total)" tooltip="Total hours across all shifts in this period" />
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatCard label="Hours — Staff" value={fmt(shiftStats.filledMins)} color={GREEN} sub={`${pct(shiftStats.filledMins, shiftStats.totalMins)}% of requested`} tooltip="Hours fulfilled by internal staff" />
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatCard label="Hours — Agency" value={fmt(shiftStats.agencyMins)} color={AMBER} sub={`${pct(shiftStats.agencyMins, shiftStats.totalMins)}% of requested`} tooltip="Hours fulfilled by agency staff" />
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatCard label="Hours — Unfilled" value={fmt(shiftStats.openMins)} color={RED} sub={`${pct(shiftStats.openMins, shiftStats.totalMins)}% of requested`} tooltip="Hours still needing cover" />
            </Grid>
          </Grid>

          {/* ══ SECTION 3: Availability Overview ═══════════════════════════ */}
          <SectionTitle icon={EventAvailableIcon} label="Availability Overview" />
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={4} md={2}>
              <StatCard label="Availability Records" value={availStats.available} color={TEAL} sub={`${fmt(availStats.totalMins)} offered`} tooltip="Total availability submissions (excluding unavailability)" />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <StatCard label="Approved" value={availStats.approved} color={GREEN} sub={`${availStats.pctApproved}% · ${fmt(availStats.approvedMins)}`} tooltip="Approved availability records" />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <StatCard label="Pending" value={availStats.pending} color={AMBER} sub={`${availStats.pctPending}% of submitted`} tooltip="Availability records awaiting review" />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <StatCard label="Rejected" value={availStats.rejected} color={RED} sub={`${availStats.pctRejected}% of submitted`} tooltip="Rejected availability records" />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <StatCard label="Unavailability" value={availStats.unavail} color={`${RED}bb`} sub={`${fmt(availStats.unavailMins)} blocked`} tooltip="Records marked as unavailable (not sick leave)" />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <StatCard label="Sick Leave" value={availStats.sick} color={RED} sub={`${fmt(availStats.sickMins)} absent`} tooltip="Sick leave records in this period" icon={LocalHospitalIcon} />
            </Grid>
          </Grid>

          {/* Availability breakdown + utilisation + reasons */}
          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid item xs={12} md={4}>
              <Paper elevation={0} sx={{ bgcolor: SLATE, border: `1px solid ${TEAL}22`, borderRadius: 2, p: 2.5, height: '100%' }}>
                <Typography sx={{ fontSize: '0.7rem', letterSpacing: 1.5, textTransform: 'uppercase', color: MUTED, fontFamily: '"DM Mono", monospace', mb: 2 }}>Availability Status</Typography>
                <BarRow label="Approved"      value={availStats.approved} total={availStats.available || 1} color={GREEN} />
                <BarRow label="Pending"       value={availStats.pending}  total={availStats.available || 1} color={AMBER} />
                <BarRow label="Rejected"      value={availStats.rejected} total={availStats.available || 1} color={RED} />
                <Divider sx={{ borderColor: `${TEAL}22`, my: 1.5 }} />
                <BarRow label="Unavailability" value={availStats.unavail} total={(availStats.available + availStats.unavail + availStats.sick) || 1} color={`${RED}aa`} />
                <BarRow label="Sick Leave"     value={availStats.sick}    total={(availStats.available + availStats.unavail + availStats.sick) || 1} color={RED} />
                <Box sx={{ mt: 2, pt: 1.5, borderTop: `1px solid ${TEAL}22` }}>
                  <Typography sx={{ fontSize: '0.68rem', color: MUTED, fontFamily: '"DM Mono", monospace' }}>
                    Staff with availability: <span style={{ color: WHITE, fontWeight: 700 }}>{availStats.staffWithAvailCount}</span>
                  </Typography>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} md={4}>
              <Paper elevation={0} sx={{ bgcolor: SLATE, border: `1px solid ${TEAL}22`, borderRadius: 2, p: 2.5, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                <Typography sx={{ fontSize: '0.7rem', letterSpacing: 1.5, textTransform: 'uppercase', color: MUTED, fontFamily: '"DM Mono", monospace', alignSelf: 'flex-start' }}>Utilisation</Typography>
                <GaugeRing value={availStats.utilisationPct} label="Availability Utilisation" color={availStats.utilisationPct >= 80 ? GREEN : availStats.utilisationPct >= 50 ? AMBER : RED} size={120} />
                <Box sx={{ width: '100%' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography sx={{ fontSize: '0.7rem', color: MUTED, fontFamily: '"DM Mono", monospace' }}>Approved availability</Typography>
                    <Typography sx={{ fontSize: '0.7rem', color: WHITE, fontFamily: '"DM Mono", monospace' }}>{fmt(availStats.approvedMins)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography sx={{ fontSize: '0.7rem', color: MUTED, fontFamily: '"DM Mono", monospace' }}>Staff hours filled</Typography>
                    <Typography sx={{ fontSize: '0.7rem', color: GREEN, fontFamily: '"DM Mono", monospace' }}>{fmt(shiftStats.filledMins)}</Typography>
                  </Box>
                  {coverageGap.gapMins > 0 && (
                    <Alert severity="warning" icon={<TrendingDownIcon />} sx={{ mt: 1, bgcolor: `${AMBER}11`, color: AMBER, border: `1px solid ${AMBER}33`, fontFamily: '"DM Mono", monospace', fontSize: '0.7rem', p: '4px 10px',
                      '& .MuiAlert-icon': { color: AMBER, fontSize: 16 } }}>
                      {fmt(coverageGap.gapMins)} demand exceeds approved availability
                    </Alert>
                  )}
                  {coverageGap.surplusMins > 0 && (
                    <Alert severity="success" icon={<TrendingUpIcon />} sx={{ mt: 1, bgcolor: `${GREEN}11`, color: GREEN, border: `1px solid ${GREEN}33`, fontFamily: '"DM Mono", monospace', fontSize: '0.7rem', p: '4px 10px',
                      '& .MuiAlert-icon': { color: GREEN, fontSize: 16 } }}>
                      {fmt(coverageGap.surplusMins)} surplus availability over demand
                    </Alert>
                  )}
                </Box>
                <Tooltip title="Utilisation = Staff-filled shift hours ÷ Approved availability hours × 100. Shows how effectively available staff time is being used." arrow>
                  <Typography sx={{ fontSize: '0.62rem', color: MUTED, fontFamily: '"DM Mono", monospace', cursor: 'help', textAlign: 'center' }}>
                    ℹ︎ Staff-filled hours ÷ approved availability hours
                  </Typography>
                </Tooltip>
              </Paper>
            </Grid>

            <Grid item xs={12} md={4}>
              <Paper elevation={0} sx={{ bgcolor: SLATE, border: `1px solid ${TEAL}22`, borderRadius: 2, p: 2.5, height: '100%' }}>
                <Typography sx={{ fontSize: '0.7rem', letterSpacing: 1.5, textTransform: 'uppercase', color: MUTED, fontFamily: '"DM Mono", monospace', mb: 2 }}>
                  Top Unavailability Reasons
                </Typography>
                {availStats.unavailReasons.length === 0
                  ? <Typography sx={{ color: MUTED, fontSize: '0.75rem', fontFamily: '"DM Mono", monospace' }}>None recorded</Typography>
                  : availStats.unavailReasons.map(([reason, count]) => (
                    <BarRow key={reason} label={reason} value={count} total={availStats.unavail + availStats.sick || 1} color={RED} />
                  ))}
                <Divider sx={{ borderColor: `${TEAL}22`, my: 1.5 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography sx={{ fontSize: '0.68rem', color: MUTED, fontFamily: '"DM Mono", monospace' }}>Total unavailability hours</Typography>
                  <Typography sx={{ fontSize: '0.68rem', color: RED, fontFamily: '"DM Mono", monospace', fontWeight: 700 }}>{fmt(availStats.unavailMins + availStats.sickMins)}</Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>

          {/* ══ SECTION 4: Key Performance Indicators ═══════════════════════ */}
          <SectionTitle icon={CheckCircleOutlineIcon} label="Key Performance Indicators" />
          <Grid container spacing={2} sx={{ mb: 4 }}>
            {/* Agency dependency */}
            <Grid item xs={12} sm={6} md={3}>
              <Paper elevation={0} sx={{ bgcolor: SLATE, border: `1px solid ${AMBER}33`, borderRadius: 2, p: 2.5 }}>
                <Typography sx={{ fontSize: '0.68rem', letterSpacing: 1.5, textTransform: 'uppercase', color: MUTED, fontFamily: '"DM Mono", monospace', mb: 1 }}>Agency Dependency</Typography>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                  <Typography sx={{ fontSize: '2.5rem', fontWeight: 700, color: shiftStats.pctAgency > 30 ? RED : shiftStats.pctAgency > 15 ? AMBER : GREEN, fontFamily: '"DM Mono", monospace', lineHeight: 1 }}>
                    {shiftStats.pctAgency}%
                  </Typography>
                  {shiftStats.pctAgency > 15
                    ? <TrendingUpIcon sx={{ color: RED, fontSize: 20 }} />
                    : <TrendingDownIcon sx={{ color: GREEN, fontSize: 20 }} />}
                </Box>
                <Typography sx={{ fontSize: '0.7rem', color: MUTED, fontFamily: '"DM Mono", monospace', mt: 0.5 }}>
                  {shiftStats.agency} of {shiftStats.total} shifts via agency
                </Typography>
                <Box sx={{ mt: 1.5, p: 1, bgcolor: shiftStats.pctAgency > 30 ? `${RED}11` : `${TEAL}11`, borderRadius: 1 }}>
                  <Typography sx={{ fontSize: '0.65rem', color: shiftStats.pctAgency > 30 ? RED : shiftStats.pctAgency > 15 ? AMBER : GREEN, fontFamily: '"DM Mono", monospace' }}>
                    {shiftStats.pctAgency > 30 ? '⚠ HIGH — review staffing levels' : shiftStats.pctAgency > 15 ? '△ MODERATE — monitor closely' : '✓ WITHIN TARGET'}
                  </Typography>
                </Box>
              </Paper>
            </Grid>

            {/* Unfilled shift rate */}
            <Grid item xs={12} sm={6} md={3}>
              <Paper elevation={0} sx={{ bgcolor: SLATE, border: `1px solid ${RED}33`, borderRadius: 2, p: 2.5 }}>
                <Typography sx={{ fontSize: '0.68rem', letterSpacing: 1.5, textTransform: 'uppercase', color: MUTED, fontFamily: '"DM Mono", monospace', mb: 1 }}>Unfilled Shift Rate</Typography>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                  <Typography sx={{ fontSize: '2.5rem', fontWeight: 700, color: shiftStats.pctOpen > 20 ? RED : shiftStats.pctOpen > 10 ? AMBER : GREEN, fontFamily: '"DM Mono", monospace', lineHeight: 1 }}>
                    {shiftStats.pctOpen}%
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: '0.7rem', color: MUTED, fontFamily: '"DM Mono", monospace', mt: 0.5 }}>
                  {shiftStats.open} shifts open · {fmt(shiftStats.openMins)} uncovered
                </Typography>
                <Box sx={{ mt: 1.5, p: 1, bgcolor: shiftStats.pctOpen > 20 ? `${RED}11` : `${TEAL}11`, borderRadius: 1 }}>
                  <Typography sx={{ fontSize: '0.65rem', color: shiftStats.pctOpen > 20 ? RED : shiftStats.pctOpen > 10 ? AMBER : GREEN, fontFamily: '"DM Mono", monospace' }}>
                    {shiftStats.pctOpen > 20 ? '⚠ HIGH — escalate to management' : shiftStats.pctOpen > 10 ? '△ MODERATE — action needed' : '✓ WITHIN ACCEPTABLE RANGE'}
                  </Typography>
                </Box>
              </Paper>
            </Grid>

            {/* Staff coverage rate */}
            <Grid item xs={12} sm={6} md={3}>
              <Paper elevation={0} sx={{ bgcolor: SLATE, border: `1px solid ${GREEN}33`, borderRadius: 2, p: 2.5 }}>
                <Typography sx={{ fontSize: '0.68rem', letterSpacing: 1.5, textTransform: 'uppercase', color: MUTED, fontFamily: '"DM Mono", monospace', mb: 1 }}>Internal Staff Coverage</Typography>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                  <Typography sx={{ fontSize: '2.5rem', fontWeight: 700, color: shiftStats.pctFilled >= 70 ? GREEN : shiftStats.pctFilled >= 50 ? AMBER : RED, fontFamily: '"DM Mono", monospace', lineHeight: 1 }}>
                    {shiftStats.pctFilled}%
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: '0.7rem', color: MUTED, fontFamily: '"DM Mono", monospace', mt: 0.5 }}>
                  {shiftStats.filled} shifts · {shiftStats.staffCount} staff deployed
                </Typography>
                <Box sx={{ mt: 1.5, p: 1, bgcolor: shiftStats.pctFilled >= 70 ? `${GREEN}11` : `${RED}11`, borderRadius: 1 }}>
                  <Typography sx={{ fontSize: '0.65rem', color: shiftStats.pctFilled >= 70 ? GREEN : shiftStats.pctFilled >= 50 ? AMBER : RED, fontFamily: '"DM Mono", monospace' }}>
                    {shiftStats.pctFilled >= 70 ? '✓ STRONG INTERNAL COVERAGE' : shiftStats.pctFilled >= 50 ? '△ MODERATE — review allocation' : '⚠ LOW — increase recruitment/rostering'}
                  </Typography>
                </Box>
              </Paper>
            </Grid>

            {/* Availability utilisation KPI */}
            <Grid item xs={12} sm={6} md={3}>
              <Paper elevation={0} sx={{ bgcolor: SLATE, border: `1px solid ${TEAL}33`, borderRadius: 2, p: 2.5 }}>
                <Typography sx={{ fontSize: '0.68rem', letterSpacing: 1.5, textTransform: 'uppercase', color: MUTED, fontFamily: '"DM Mono", monospace', mb: 1 }}>Availability Utilisation</Typography>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                  <Typography sx={{ fontSize: '2.5rem', fontWeight: 700, color: availStats.utilisationPct >= 70 ? GREEN : availStats.utilisationPct >= 40 ? AMBER : RED, fontFamily: '"DM Mono", monospace', lineHeight: 1 }}>
                    {availStats.utilisationPct}%
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: '0.7rem', color: MUTED, fontFamily: '"DM Mono", monospace', mt: 0.5 }}>
                  {fmt(shiftStats.filledMins)} used of {fmt(availStats.approvedMins)} available
                </Typography>
                <Box sx={{ mt: 1.5, p: 1, bgcolor: availStats.utilisationPct >= 70 ? `${GREEN}11` : `${AMBER}11`, borderRadius: 1 }}>
                  <Typography sx={{ fontSize: '0.65rem', color: availStats.utilisationPct >= 70 ? GREEN : availStats.utilisationPct >= 40 ? AMBER : RED, fontFamily: '"DM Mono", monospace' }}>
                    {availStats.utilisationPct >= 70 ? '✓ EFFICIENT USE OF AVAILABILITY' : availStats.utilisationPct >= 40 ? '△ MODERATE UTILISATION' : '⚠ LOW — availability not being converted to shifts'}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>

          {/* ══ SECTION 5: By Staff Category ════════════════════════════════ */}
          {shiftStats.byCategory.length > 0 && (
            <>
              <SectionTitle icon={GroupIcon} label="By Staff Category" />
              <Grid container spacing={2} sx={{ mb: 4 }}>
                <Grid item xs={12} md={8}>
                  <Paper elevation={0} sx={{ bgcolor: SLATE, border: `1px solid ${TEAL}22`, borderRadius: 2, p: 2.5 }}>
                    <Typography sx={{ fontSize: '0.7rem', letterSpacing: 1.5, textTransform: 'uppercase', color: MUTED, fontFamily: '"DM Mono", monospace', mb: 2 }}>Shifts by Staff Category</Typography>
                    {shiftStats.byCategory.map(([cat, d]) => (
                      <Box key={cat} sx={{ mb: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.4 }}>
                          <Typography sx={{ fontSize: '0.72rem', color: WHITE, fontFamily: '"DM Mono", monospace' }}>{cat}</Typography>
                          <Box sx={{ display: 'flex', gap: 2 }}>
                            <Typography sx={{ fontSize: '0.72rem', color: TEAL_L, fontFamily: '"DM Mono", monospace' }}>{d.total} total</Typography>
                            <Typography sx={{ fontSize: '0.72rem', color: GREEN, fontFamily: '"DM Mono", monospace' }}>{pct(d.filled, d.total)}% filled</Typography>
                          </Box>
                        </Box>
                        <Box sx={{ height: 6, bgcolor: `${TEAL}22`, borderRadius: 1, overflow: 'hidden' }}>
                          <Box sx={{ height: '100%', width: `${pct(d.filled, d.total)}%`, bgcolor: GREEN, borderRadius: 1 }} />
                        </Box>
                      </Box>
                    ))}
                  </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Paper elevation={0} sx={{ bgcolor: SLATE, border: `1px solid ${TEAL}22`, borderRadius: 2, p: 2.5, height: '100%' }}>
                    <Typography sx={{ fontSize: '0.7rem', letterSpacing: 1.5, textTransform: 'uppercase', color: MUTED, fontFamily: '"DM Mono", monospace', mb: 2 }}>Hours by Category</Typography>
                    {shiftStats.byCategory.map(([cat, d]) => (
                      <Box key={cat} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography sx={{ fontSize: '0.72rem', color: MUTED, fontFamily: '"DM Mono", monospace', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cat}</Typography>
                        <Typography sx={{ fontSize: '0.72rem', color: WHITE, fontFamily: '"DM Mono", monospace', fontWeight: 700 }}>
                          {fmt(filteredShifts.filter(s => s.categoryOfStaff === cat).reduce((a, s) => a + shiftMins(s), 0))}
                        </Typography>
                      </Box>
                    ))}
                  </Paper>
                </Grid>
              </Grid>
            </>
          )}

          {/* Footer */}
          <Box sx={{ textAlign: 'center', pt: 2, pb: 1 }}>
            <Typography sx={{ fontSize: '0.62rem', color: `${MUTED}88`, fontFamily: '"DM Mono", monospace', letterSpacing: 1 }}>
              BEAUMONT HOSPITAL STAFF SCHEDULING SYSTEM · DATA REFLECTS SELECTED DATE RANGE AND FILTERS · {new Date().toLocaleString()}
            </Typography>
          </Box>

        </Box>
      )}
    </Box>
  );
};

export default SchedulingDashboard;